import axios from "axios"
import { NextResponse } from "next/server"
import { z } from "zod"
import { env } from "@/env"
import { getSession } from "@/services/auth-service"

const NO_CACHE_HEADERS = {
  "Cache-Control": "no-store",
} as const

const uploadSchema = z.object({
  productId: z.number(),
  nonce: z.string(),
})

export async function POST(request: Request) {
  const session = await getSession()
  const sessionCookie = session?.wordpressSessionCookie?.trim()

  if (!(sessionCookie && session?.user)) {
    return NextResponse.json(
      {
        ok: false,
        code: "UNAUTHORIZED",
        message: "Authentication required",
      },
      {
        status: 401,
        headers: NO_CACHE_HEADERS,
      }
    )
  }

  try {
    const formData = await request.formData()
    const file = formData.get("file") as File | null
    const productIdStr = formData.get("productId") as string | null
    const nonce = formData.get("nonce") as string | null

    if (!(file && productIdStr && nonce)) {
      return NextResponse.json(
        {
          ok: false,
          code: "INVALID_PAYLOAD",
          message: "Missing required fields",
        },
        {
          status: 400,
          headers: NO_CACHE_HEADERS,
        }
      )
    }

    const productId = Number(productIdStr)
    if (Number.isNaN(productId)) {
      return NextResponse.json(
        {
          ok: false,
          code: "INVALID_PAYLOAD",
          message: "Invalid product ID",
        },
        {
          status: 400,
          headers: NO_CACHE_HEADERS,
        }
      )
    }

    // Create form data for WordPress
    const wpFormData = new FormData()
    wpFormData.append("cr_file", file)
    wpFormData.append("cr_postid", String(productId))
    wpFormData.append("cr_nonce", nonce)

    // WordPress AJAX endpoints are at root level, NOT under /wp-json
    const wpBaseUrl = env.WP_API_URL.replace(/\/wp-json\/?$/, "")
    const uploadUrl = `${wpBaseUrl}/wp-admin/admin-ajax.php?action=cr_upload_local_images_frontend`

    console.log("[Upload Route] Uploading to:", uploadUrl)

    const response = await axios.post<{
      code: number
      message?: string
      attachment?: {
        id: number
        key: string
        nonce: string
      }
    }>(uploadUrl, wpFormData, {
      headers: {
        "Content-Type": "multipart/form-data",
        Cookie: sessionCookie,
      },
    })

    console.log(
      "[Upload Route] WordPress response:",
      response.status,
      response.data
    )

    if (response.data.code !== 200 || !response.data.attachment) {
      return NextResponse.json(
        {
          ok: false,
          code: "UPLOAD_FAILED",
          message: response.data.message || "Upload failed",
        },
        {
          status: 400,
          headers: NO_CACHE_HEADERS,
        }
      )
    }

    return NextResponse.json(
      {
        ok: true,
        attachment: response.data.attachment,
      },
      {
        headers: NO_CACHE_HEADERS,
      }
    )
  } catch (error) {
    console.error("[Media Upload] Upload error:", error)
    return NextResponse.json(
      {
        ok: false,
        code: "UPLOAD_FAILED",
        message: "Failed to upload media",
      },
      {
        status: 502,
        headers: NO_CACHE_HEADERS,
      }
    )
  }
}
